import { useEffect, useState } from 'react';
import { MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { Progress, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps, useTemplateSecrets } from '@backstage/plugin-scaffolder-react';

type IacRepo = { repository: string; environments: string[] };
type SecretBlock = { file: string; block: string; keys: string[] };
export type EnvValues = { secrets: string[]; variables: Record<string, string>; wired?: string[]; blocks?: SecretBlock[] };
type Wiring = { secretFile?: string; secretBlock?: string; secretKey?: string };
type Kind = 'secret' | 'variable';

/** Igual que el backend (iacEnvValue.ts): un nombre vive como secreto o como variable, nunca en los dos. */
export const kindOf = (name: string, values: EnvValues): Kind | undefined => {
  if (values.secrets.includes(name)) return 'secret';
  return name in values.variables ? 'variable' : undefined;
};

const label = (kind: Kind) => (kind === 'secret' ? 'secreto' : 'variable');

const useJson = <T,>(path: string | undefined) => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (!path) return;
    setData(undefined);
    setError(undefined);
    (async () => {
      const res = await fetch(`${await discovery.getBaseUrl('argo-values')}${path}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      setData(body);
    })().catch(e => setError(String(e?.message ?? e)));
  }, [path, discovery, fetch]);
  return { data, error };
};

/** Repo IaC: desplegable con los repos ya migrados (tienen age-recipient.txt) y sus ambientes. */
export const IacRepositoryField = ({ formData, onChange, rawErrors, schema }: FieldExtensionComponentProps<string>) => {
  const { data, error } = useJson<IacRepo[]>('/iac/repos');
  if (error) return <WarningPanel title="No se pudieron leer los repos IaC" message={error} />;
  if (!data) return <Progress />;
  return (
    <TextField
      select
      fullWidth
      required
      label={schema.title ?? 'Repositorio IaC'}
      value={formData ?? ''}
      error={!!rawErrors?.length}
      helperText={rawErrors?.[0] ?? `${data.length} repos migrados. Si falta el tuyo, pídeselo a devops.`}
      onChange={e => onChange(e.target.value)}
    >
      {data.map(r => (
        <MenuItem key={r.repository} value={r.repository}>
          {r.repository} <Typography component="span" variant="body2" color="textSecondary">&nbsp;· {r.environments.join(', ')}</Typography>
        </MenuItem>
      ))}
    </TextField>
  );
};

const valuesPath = (form: any) =>
  form.repository && form.environment
    ? `/iac/values?repository=${encodeURIComponent(form.repository)}&environment=${encodeURIComponent(form.environment)}`
    : undefined;

// Lo que vio el campo, para que la validación del formulario no deje seguir con un nombre del otro tipo.
const seen = new Map<string, EnvValues | string>();

/** Nombre: muestra los nombres que ya existen en el repo y ambiente elegidos (solo nombres), filtrados por lo que se escribe. */
export const IacValueNameField = ({ formData, onChange, formContext, rawErrors, schema }: FieldExtensionComponentProps<string>) => {
  const form = (formContext?.formData ?? {}) as any;
  const key = form.repository && form.environment ? `${form.repository}/${form.environment}` : undefined;
  const { data, error } = useJson<EnvValues>(valuesPath(form));
  useEffect(() => {
    if (key && (data || error)) seen.set(key, data ?? error!);
  }, [key, data, error]);

  const name = formData ?? '';
  const existing = data ? kindOf(name, data) : undefined;
  const rows = data
    ? [...data.secrets.map(n => ({ n, k: 'secret' as Kind })), ...Object.keys(data.variables).map(n => ({ n, k: 'variable' as Kind }))]
        .filter(r => !name || r.n.includes(name))
        .sort((a, b) => a.n.localeCompare(b.n))
    : [];
  let help = rawErrors?.[0] ?? schema.description ?? 'El nombre del token #{NOMBRE}# en los .tfvars (mayúsculas, números y _).';
  if (existing) help = existing === form.kind ? `Ya existe: se actualizará este ${label(existing)}` : `Ya es un ${label(existing)}: cambia el tipo`;

  return (
    <div>
      <TextField
        fullWidth
        required
        label={schema.title ?? 'Nombre'}
        placeholder="REDIS_PASSWORD"
        value={name}
        error={(!!existing && existing !== form.kind) || !!rawErrors?.length}
        helperText={help}
        onChange={e => onChange(e.target.value.trim().toUpperCase())}
      />
      {!key && <Typography color="textSecondary" variant="body2" style={{ marginTop: 8 }}>Elige el repo y el ambiente para ver los nombres que ya tiene.</Typography>}
      {key && error && <WarningPanel title="No se pudieron leer los nombres" message={error} />}
      {key && !error && !data && <Progress />}
      {data && (
        <div style={{ marginTop: 12 }}>
          <Typography variant="body2" color="textSecondary">
            {data.secrets.length} secretos y {Object.keys(data.variables).length} variables en <b>{form.environment}</b>
            {name ? ` · ${rows.length} coinciden con "${name}"` : ''}. Los secretos no se pueden leer; las variables sí.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Valor actual</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow
                  key={`${r.k}${r.n}`}
                  hover
                  style={{ cursor: 'pointer', background: r.n === name ? 'rgba(33, 150, 243, 0.15)' : undefined }}
                  onClick={() => onChange(r.n)}
                >
                  <TableCell style={{ fontFamily: 'monospace' }}>{r.n}</TableCell>
                  <TableCell>{label(r.k)}</TableCell>
                  <TableCell style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {r.k === 'variable' ? data.variables[r.n] : '•••• (cifrado)'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

/** No deja seguir si el ambiente no existe en el repo o el nombre ya es del otro tipo (el backend lo vuelve a comprobar). */
export const validateIacValueName = (value: string | undefined, validation: { addError: (m: string) => void }, context: { formData: any }) => {
  const { repository, environment, kind } = context?.formData ?? {};
  const values = seen.get(`${repository}/${environment}`);
  if (typeof values === 'string') {
    validation.addError(values);
    return;
  }
  const existing = values && value ? kindOf(value, values) : undefined;
  if (existing && existing !== kind) validation.addError(`${value} ya es un ${label(existing)} en ${environment}: cambia el tipo`);
};

let prefilledFor: string | undefined;

/**
 * Valor: un secreto se escribe oculto, como el campo Secret; una variable se muestra con su valor actual para editarlo.
 * Los dos van en secrets.value (la plantilla no cambia); solo la variable queda además en claro en el formulario.
 */
export const IacValueField = ({ name, onChange, formContext, rawErrors, schema }: FieldExtensionComponentProps<string>) => {
  const { secrets, setSecrets } = useTemplateSecrets();
  const form = (formContext?.formData ?? {}) as any;
  const isSecret = form.kind !== 'variable';
  const { data, error } = useJson<EnvValues>(isSecret ? undefined : valuesPath(form));
  const current = data?.variables[form.name];
  const [value, setValue] = useState<string>(secrets[name] ?? '');

  const set = (v: string) => {
    setValue(v);
    setSecrets({ [name]: v });
    onChange(isSecret ? '*'.repeat(v.length) : v);
  };
  // Con cada variable elegida se precarga su valor actual (vacío si es nueva); al volver al paso se respeta lo escrito.
  const key = `${form.repository}/${form.environment}/${form.name}`;
  useEffect(() => {
    if (isSecret || !data || prefilledFor === key) return;
    prefilledFor = key;
    set(current ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, key]);

  let help = rawErrors?.[0] ?? 'Pégalo tal cual. Se cifra antes de salir de Backstage y nadie lo puede leer en el PR.';
  if (!isSecret) {
    if (current === undefined) help = rawErrors?.[0] ?? `Variable nueva en ${form.environment}: queda en claro en variables.json.`;
    else help = rawErrors?.[0] ?? (value === current ? 'Es el valor actual: cámbialo, o el PR no tendrá cambios.' : `Valor actual: ${current}`);
  }

  return (
    <div>
      <TextField
        fullWidth
        required
        multiline={!isSecret}
        type={isSecret ? 'password' : 'text'}
        autoComplete="off"
        label={schema.title ?? 'Valor'}
        value={value}
        error={!!rawErrors?.length}
        helperText={help}
        onChange={e => set(e.target.value)}
      />
      {!isSecret && error && <WarningPanel title="No se pudo leer el valor actual" message={error} />}
      {!isSecret && !error && !data && valuesPath(form) && <Progress />}
    </div>
  );
};

const NO_WIRING = '';

/**
 * Secreto nuevo que ningún .tfvars usa: elige en qué Secret de Kubernetes (bloque del mapa `secrets`) y con qué clave
 * entra, y el PR añade la línea en el .tfvars. Si ya lo usa Terraform, o es una variable, no pide nada.
 */
export const IacSecretWiringField = ({ formData, onChange, formContext, schema }: FieldExtensionComponentProps<Wiring>) => {
  const form = (formContext?.formData ?? {}) as any;
  const { data, error } = useJson<EnvValues>(valuesPath(form));
  const name: string = form.name ?? '';
  const needed = form.kind === 'secret' && !!name && !!data && !data.wired?.includes(name);
  const blocks = data?.blocks ?? [];
  const id = (b: SecretBlock) => `${b.file}#${b.block}`;
  const selected = formData?.secretBlock ? `${formData.secretFile}#${formData.secretBlock}` : NO_WIRING;
  // Si deja de hacer falta (cambió el nombre o el tipo), no se manda un bloque viejo.
  useEffect(() => {
    if (!needed && formData?.secretBlock) onChange({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needed]);

  if (!form.repository || !form.environment || form.kind !== 'secret' || !name) return null;
  if (error) return <WarningPanel title="No se pudo leer qué usa Terraform" message={error} />;
  if (!data) return <Progress />;
  if (!needed) {
    return (
      <Typography variant="body2" color="textSecondary">
        Terraform ya usa <code>{`#{${name}}#`}</code>: el PR solo cambia el valor.
      </Typography>
    );
  }
  if (!blocks.length) {
    return (
      <WarningPanel severity="info" title={`Ningún .tfvars usa #{${name}}#`}>
        Este repo no tiene el mapa <code>secrets</code> en sus .tfvars: el valor queda guardado y devops lo conecta a Terraform.
      </WarningPanel>
    );
  }
  const block = blocks.find(b => id(b) === selected);
  const key = formData?.secretKey || name;
  return (
    <div>
      <TextField
        select
        fullWidth
        label={schema.title ?? 'Secret de Kubernetes donde va'}
        value={selected}
        helperText={`Ningún .tfvars usa #{${name}}#. El PR añade la línea en el mapa secrets (aprueba también devops).`}
        onChange={e => {
          const b = blocks.find(x => id(x) === e.target.value);
          onChange(b ? { secretFile: b.file, secretBlock: b.block, secretKey: formData?.secretKey } : {});
        }}
      >
        <MenuItem value={NO_WIRING}>No conectar ahora (lo hace devops)</MenuItem>
        {blocks.map(b => (
          <MenuItem key={id(b)} value={id(b)}>
            {b.block} <Typography component="span" variant="body2" color="textSecondary">&nbsp;· {b.file} · {b.keys.length} claves</Typography>
          </MenuItem>
        ))}
      </TextField>
      {block && (
        <TextField
          fullWidth
          style={{ marginTop: 12 }}
          label="Clave dentro del Secret"
          placeholder={name}
          value={formData?.secretKey ?? ''}
          error={block.keys.includes(key)}
          helperText={
            block.keys.includes(key)
              ? `${block.block} ya tiene ${key}: elige otra clave`
              : `La variable de entorno que verá la app. Vacío = ${name}.`
          }
          onChange={e => onChange({ ...formData, secretKey: e.target.value.trim() || undefined })}
        />
      )}
    </div>
  );
};
